import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';

import {
  AuthBaseUrlContextProvider,
  type GraphQLClient,
  type GraphQLClientError,
} from '@opendoor/auth-fe/context';
import type { GraphQLError } from 'graphql';

import { ATHENA_URL, COSMOS_URL, DEPLOY_ENV } from '../globals';

type EndpointOptions = {
  apiEndpoint: string;
  clientName: string;
};

class FetchGqlError extends Error implements GraphQLClientError {
  graphQLErrors: readonly GraphQLError[];
  networkError: { status?: number; statusText?: string };
  constructor(
    message: string,
    errors: Array<GraphQLError>,
    networkError: { status?: number; statusText?: string },
  ) {
    super(message);
    this.graphQLErrors = errors;
    this.networkError = networkError;
  }
}

const getQueryOperationName = (query: string) => {
  return query.match(/query\s+(?<operationName>[^{(\s]+)/)?.groups?.operationName ?? null;
};

const fetchClient: (opts: EndpointOptions) => GraphQLClient = (opts) => {
  return {
    async query<TData, TVars>(query: string, vars: TVars) {
      const body = {
        operationName: getQueryOperationName(query),
        query: query,
        variables: vars,
      };

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'apollographql-client-name': opts.clientName,
      };

      // Add dev auth bypass header for local development
      // This allows local Cosmos to authenticate with local Athena
      // without needing real session cookies
      const devUserToken = process.env.NEXT_PUBLIC_ATHENA_DEV_USER_TOKEN;
      if (DEPLOY_ENV === 'development' && devUserToken) {
        headers['x-athena-request-user'] = devUserToken;
      }

      const res = await fetch(opts.apiEndpoint, {
        credentials: 'include',
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        mode: 'cors',
      });
      if (!res.ok) {
        return {
          error: new FetchGqlError('Failed to fetch', [], {
            status: res.status,
            statusText: res.statusText,
          }),
          data: undefined,
        };
      }
      const json = await res.json();
      if (json.errors) {
        return {
          error: new FetchGqlError('Graphql request had errors', json.errors, {}),
          data: undefined,
        };
      }
      return {
        data: json.data as TData,
        error: undefined,
      };
    },
  };
};

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const fetchClientMemo = useMemo(
    () => fetchClient({ apiEndpoint: ATHENA_URL, clientName: 'cosmos' }),
    [],
  );
  return (
    <AuthBaseUrlContextProvider authBaseUrl={COSMOS_URL} client={fetchClientMemo}>
      {children}
    </AuthBaseUrlContextProvider>
  );
};

export default AuthProvider;
